import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { typeIntoBox, pasteIntoBoxes } from '../utils/otp';

// ── Password strength scorer ──────────────────────────────────────────────────
const scorePassword = (pw) => {
  let s = 0;
  if (pw.length >= 8)  s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
};
const STRENGTH = [
  null,
  { label: 'Too weak',          color: 'bg-red-500',    text: 'text-red-500',    bars: 1 },
  { label: 'Could be stronger', color: 'bg-orange-400', text: 'text-orange-400', bars: 2 },
  { label: 'Good password',     color: 'bg-blue-500',   text: 'text-blue-500',   bars: 3 },
  { label: 'Strong password',   color: 'bg-green-500',  text: 'text-green-500',  bars: 4 },
  { label: 'Strong password',   color: 'bg-green-500',  text: 'text-green-500',  bars: 4 },
];

// ── Shared page wrapper / nav ─────────────────────────────────────────────────
const AuthNav = () => (
  <nav className="bg-white border-b border-border px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-10">
    <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-primary transition-colors">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
      </svg>
      Back to Home
    </Link>
    <Link to="/shop" className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
      Browse Products
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
      </svg>
    </Link>
  </nav>
);

const Logo = () => (
  <Link to="/" className="inline-block">
    <div className="leading-none text-center">
      <div className="text-2xl font-black tracking-tight text-primary">SKINN</div>
      <div className="text-[0.5rem] font-bold tracking-[0.35em] text-accent -mt-0.5 uppercase">Thrive</div>
    </div>
  </Link>
);

const Spinner = () => (
  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

// ── Step 1: Account details form ──────────────────────────────────────────────
const AccountDetailsStep = ({ onSuccess }) => {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = form.password ? STRENGTH[Math.min(scorePassword(form.password), 4)] : null;

  const validate = useCallback((field, value, allForm) => {
    const f = allForm || form;
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return '';
      case 'confirm':
        if (!value) return 'Please confirm your password';
        if (value !== f.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  }, [form]);

  const handleBlur = (field) => {
    setTouched(t => ({ ...t, [field]: true }));
    setErrors(e => ({ ...e, [field]: validate(field, form[field]) }));
  };

  const handleChange = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    if (touched[field]) {
      setErrors(e => ({ ...e, [field]: validate(field, value, newForm) }));
    }
    // Re-validate confirm if password changes
    if (field === 'password' && touched.confirm) {
      setErrors(e => ({ ...e, confirm: validate('confirm', newForm.confirm, newForm) }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    // Touch all fields to show errors
    const allTouched = { name: true, email: true, password: true, confirm: true };
    setTouched(allTouched);
    const allErrors = {
      name:     validate('name', form.name),
      email:    validate('email', form.email),
      password: validate('password', form.password),
      confirm:  validate('confirm', form.confirm),
    };
    setErrors(allErrors);
    if (Object.values(allErrors).some(Boolean)) return;

    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      onSuccess(form.email);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Registration failed';
      if (msg.toLowerCase().includes('email')) {
        setErrors(e => ({ ...e, email: msg }));
        setTouched(t => ({ ...t, email: true }));
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const FieldError = ({ field }) =>
    touched[field] && errors[field] ? (
      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
        <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
        </svg>
        {errors[field]}
      </p>
    ) : null;

  const inputClass = (field) =>
    `input-field pr-10 ${touched[field] && errors[field] ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`;

  return (
    <>
      <div className="text-center mb-6">
        <Logo />
        <h1 className="text-xl font-bold text-ink mt-3 mb-0.5">Create your account</h1>
        <p className="text-ink-soft text-sm">Join thousands of skincare enthusiasts</p>
      </div>

      <div className="bg-white rounded-3xl p-5 sm:p-8 border border-border" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Full Name</label>
            <div className="relative">
              <input
                type="text"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                placeholder="Enter your full name"
                className={inputClass('name')}
                autoComplete="name"
              />
              {touched.name && errors.name && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <FieldError field="name" />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Email Address</label>
            <div className="relative">
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="Enter your email address"
                className={inputClass('email')}
                autoComplete="email"
              />
              {touched.email && errors.email && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <FieldError field="email" />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={e => handleChange('password', e.target.value)}
                onBlur={() => handleBlur('password')}
                placeholder="Create a password (min 8 characters)"
                className={`input-field pr-20 ${touched.password && errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-ink-muted hover:text-primary transition-colors"
              >
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
            <FieldError field="password" />
            {/* Strength indicator */}
            {form.password && strength && (
              <div className="mt-2 space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= strength.bars ? strength.color : 'bg-gray-200'}`} />
                  ))}
                </div>
                <p className={`text-xs font-medium ${strength.text}`}>{strength.label}</p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={form.confirm}
                onChange={e => handleChange('confirm', e.target.value)}
                onBlur={() => handleBlur('confirm')}
                placeholder="Confirm your password"
                className={`input-field pr-20 ${touched.confirm && errors.confirm ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-ink-muted hover:text-primary transition-colors"
              >
                {showConfirm ? 'Hide' : 'Show'}
              </button>
            </div>
            <FieldError field="confirm" />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2 mt-2"
          >
            {loading ? <><Spinner /> Creating account…</> : 'Create Account →'}
          </button>
        </form>

        <p className="text-center text-sm text-ink-soft mt-5">
          Already have an account?{' '}
          <Link to="/login" className="text-primary font-semibold hover:underline">Sign in →</Link>
        </p>
      </div>
    </>
  );
};

// ── OTP Verification Step (shared by Register + Login EMAIL_NOT_VERIFIED) ─────
export const OTPVerificationStep = ({
  email,
  onSuccess,
  onBack,
  onResend,
  title = 'Verify your email',
  subtitle,
}) => {
  const { verifyEmail } = useAuth();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  // Auto-focus first box on mount
  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  // Resend countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  // typed digits move to the next box; a pasted or phone-filled code ("123456", "Your code is 123456") fills all six
  const showBoxes = (result) => {
    if (!result) return;
    setDigits(result.digits);
    setError('');
    inputRefs.current[result.focus]?.focus();
    if (result.digits.every(d => d !== '')) submitOtp(result.digits.join(''));
  };

  const handleDigitChange = (idx, val) => showBoxes(typeIntoBox(digits, idx, val));

  const handlePaste = (idx, e) => {
    e.preventDefault();
    showBoxes(pasteIntoBoxes(digits, idx, e.clipboardData.getData('text')));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const submitOtp = async (otp) => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const u = await verifyEmail(email, otp);
      onSuccess(u);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Invalid OTP. Please try again.';
      setError(msg);
      setDigits(['', '', '', '', '', '']);
      setShake(true);
      setTimeout(() => { setShake(false); inputRefs.current[0]?.focus(); }, 600);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      await onResend(email);
      toast.success('OTP resent successfully');
      setResendCooldown(30);
      setDigits(['', '', '', '', '', '']);
      setError('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const allFilled = digits.every(d => d !== '');

  return (
    <>
      <div className="text-center mb-6">
        <Logo />
        {/* Envelope icon */}
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mt-4 mb-3">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        </div>
        <h1 className="text-xl font-bold text-ink mb-1">{title}</h1>
        <p className="text-ink-soft text-sm">
          {subtitle || <>We sent a 6-digit code to <strong className="text-ink">{email}</strong></>}
        </p>
      </div>

      <div className="bg-white rounded-3xl p-5 sm:p-8 border border-border" style={{boxShadow:'0 8px 40px rgba(124,31,166,0.10)'}}>
        {/* 6-box OTP input */}
        <div className={`flex gap-2 justify-center mb-4 ${shake ? 'animate-shake' : ''}`}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${i + 1} of 6`}
              value={d}
              onChange={e => handleDigitChange(i, e.target.value)}
              onPaste={e => handlePaste(i, e)}
              onKeyDown={e => handleKeyDown(i, e)}
              onFocus={e => e.target.select()}
              className={`w-11 h-14 text-center text-xl font-bold rounded-xl border-2 transition-all outline-none
                ${d ? 'border-green-400 bg-green-50 text-green-700' : ''}
                ${error && !d ? 'border-red-400 bg-red-50' : ''}
                ${!d && !error ? 'border-border focus:border-primary focus:bg-primary-50' : ''}
              `}
            />
          ))}
        </div>

        {error && (
          <p className="text-sm text-red-500 text-center mb-4 flex items-center justify-center gap-1.5">
            <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            {error}
          </p>
        )}

        <button
          onClick={() => submitOtp(digits.join(''))}
          disabled={!allFilled || loading}
          className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2 mb-4 disabled:opacity-50"
        >
          {loading ? <><Spinner /> Verifying…</> : 'Verify & Continue →'}
        </button>

        {/* Resend */}
        <div className="text-center">
          {resendCooldown > 0 ? (
            <p className="text-sm text-ink-muted">
              Resend in{' '}
              <span className="font-semibold text-ink tabular-nums">
                0:{String(resendCooldown).padStart(2, '0')}
              </span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="text-sm text-primary font-semibold hover:underline disabled:opacity-50"
            >
              {resendLoading ? 'Resending…' : 'Resend OTP'}
            </button>
          )}
        </div>

        {/* Back link */}
        {onBack && (
          <div className="text-center mt-4">
            <button onClick={onBack} className="text-xs text-ink-muted hover:text-ink transition-colors">
              ← Wrong email? Go back
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ── Main RegisterPage ─────────────────────────────────────────────────────────
const RegisterPage = () => {
  const { resendOtp } = useAuth();
  const navigate      = useNavigate();
  const [step, setStep]   = useState('details'); // 'details' | 'otp'
  const [email, setEmail] = useState('');

  const handleDetailsSuccess = (registeredEmail) => {
    setEmail(registeredEmail);
    setStep('otp');
  };

  const handleOtpSuccess = () => {
    toast.success('Account created! Welcome to Skinn Thrive 🎉');
    navigate('/account');
  };

  return (
    <>
      <Helmet><title>Create Account — Skinn Thrive</title></Helmet>
      <AuthNav />
      <div
        className="min-h-[calc(100dvh-3.5rem)] flex items-center justify-center px-4 py-6 sm:py-12"
        style={{ background: 'linear-gradient(135deg, #F3E8FF 0%, #FCE4F3 100%)' }}
      >
        <div className="w-full max-w-md">
          {step === 'details' ? (
            <AccountDetailsStep onSuccess={handleDetailsSuccess} />
          ) : (
            <OTPVerificationStep
              email={email}
              onSuccess={handleOtpSuccess}
              onBack={() => setStep('details')}
              onResend={resendOtp}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
